<template>
  <div class="registration">
    <vue-title :title="$t('identityProvider:login:verifyOTPTitle')"></vue-title>

    <div v-if="model.showError">
      <h1 class="section-title">{{ $t('identityProvider:login:verifyOTPTitle') }}</h1>
      <div class="validation-summary-errors alert alert-danger fade show" role="alert" data-valmsg-summary="true" data-test-element="idp-alert-danger-generic">
        <i class="material-icons">warning</i>
        <span>{{ model.errorMessage }}</span>
      </div>
      <button type="button" @click="loginCancel(logoutLink)" name="Action:RedirectToLogin" class="button" :disabled="disabled">
        {{ $t('shared:action:back') }}
      </button>
    </div>

    <div v-else>
      <Form v-slot="{ validate, setErrors, setFieldError }">
        <form ref="form" action="authentication/submitverificationcode" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">
          <h1 class="section-title">{{ $t('identityProvider:login:verifyOTPTitle') }}</h1>

          <!-- Banner area for server messages -->
          <fieldset>
            <div :class="alertContent"
                 role="alert"
                 v-show="!model.showError && model.errorMessage != null"
                 data-valmsg-summary="true"
                 data-test-element="idp-alert-danger-generic">
              <i class="material-icons" v-html="messageIcon"></i>
              <span class="pad-2">{{ model.errorMessage }}</span>
              <i class="material-icons ml-auto pointer" @click.prevent="closeBanner">close</i>
            </div>

            <div class="panel-message">
              {{ otpinstructionModel.instructions }}
            </div>

            <!-- Validation summary (client-side) -->
            <div class="validation-summary-errors alert alert-danger fade show"
                 role="alert"
                 data-valmsg-summary="true"
                 v-if="hasAnyFieldErrors">
              <i class="material-icons">error</i>
              <span class="alert-message">{{ $t('validation:summary') }}</span>
            </div>

            <!-- Verification Code -->
            <div class="form-group w-50">
              <label>
                <span class="item label">{{ $t('identityProvider:login:verifyOTPCodeHeader') }}</span>
              </label>

              <Field name="verifyCode"
                     :rules="otpRules"
                     v-model="model.verificationCode"
                     v-slot="{ field, meta }">
                <input v-bind="field"
                       type="text"
                       id="txtverificationcode"
                       :maxlength="complexityOtpModel.otpMaximumLength"
                       name="verifyCode"
                       v-focus
                       class="w-100 mb-2 input-text control"
                       :class="{ 'input-validation-error': submitted && !meta.valid }"
                       autocomplete="off"
                       :aria-invalid="(submitted && !meta.valid) ? 'true' : 'false'"
                       aria-describedby="verifycode-error" />
              </Field>

              <ErrorMessage v-if="submitted" name="verifyCode" v-slot="{ message }">
                <span class="field-validation-error" id="verifycode-error" role="alert">{{ message }}</span>
              </ErrorMessage>

              <label class="resend-code-row" @click.stop>
                <a @click.prevent="isLinkEnabled ? resendVerificationCode(resendLink) : null" class="link" :class="{ 'disabled-link': !isLinkEnabled }">
                  {{ $t('identityProvider:login:verifyOTPSendAnotherCode') }}
                </a>
                <contextual-help-component :text="$t('identityProvider:login:tooltipSendAnotherCode')"
                                           data-test-element="sendanothercode-help">
                  {{ $t('identityProvider:login:tooltipSendAnotherCode') }}
                </contextual-help-component>
              </label>
            </div>

            <div class="form-actions mb-3">
              <button type="submit" class="button" :disabled="disabled">{{ $t('shared:action:continue') }}</button>
              <a @click="loginCancel(cancelLink)" class="button-secondary contextual-link" :data-test-element="'security-question-cancel'">
                {{ $t('shared:action:cancel') }}
              </a>
            </div>

            <div class="panel-message">
              <span>
                {{ $t('identityProvider:login:verifyOTPNewPhoneNumber') }}
                <router-link :to="previousPageUrl">
                  <span @click="hideToolTip" class="link link-text-transform">{{ $t('identityProvider:login:verifyOTPClickHere') }}</span>
                </router-link>
              </span>
            </div>
          </fieldset>
        </form>
      </Form>
    </div>
  </div>
</template>

<script>
  import redirectHandler from 'utils/redirect';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import ContextualHelp from 'components/popper-help';
  import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';

  export default {
    name: 'VerifyPhone',
    components: {
      'contextual-help-component': ContextualHelp,
      Form,
      Field,
      ErrorMessage
    },
    props: {
      submitVerificationCode: {
        type: Object,
        default: () => ({
          action: 'authentication/submitverificationcode',
          isOOBARegistration: true,
          challengeModel: null
        })
      },
      cancelLink: { type: String, default: 'authentication/logincancel' },
      resendLink: { type: String, default: 'authentication/sendverificationcode' },
      logoutLink: { type: String, default: 'authentication/logincancel' }
    },

    data: () => ({
      model: {
        verificationCode: null,
        showError: false,
        errorMessage: null
      },
      messageIcon: null,
      alertContent: '',
      complexityOtpModel: {},
      otpinstructionModel: {},
      previousPageUrl: '',
      submitVerificationCodeModel: null,
      isLinkEnabled: true,
      disabled: false,
      submitted: false,
      hasAnyFieldErrors: false
    }),

    computed: {
      otpRules() {
        const rules = { required: true };
        const min = Number(this.complexityOtpModel?.otpMinimumLength);
        const max = Number(this.complexityOtpModel?.otpMaximumLength);
        const pattern = this.complexityOtpModel?.otpPattern;

        if (Number.isFinite(min)) rules.min = min;
        if (Number.isFinite(max)) rules.max = max;
        if (pattern) {
          try {
            rules.regex = new RegExp(pattern);
          } catch {
            // if pattern is not a valid regex, ignore to avoid client errors
          }
        }
        return rules;
      }
    },

    methods: {
      async validateBeforeSubmit(validate, setErrors, setFieldError) {
        // reset UI state
        this.model.errorMessage = null;
        this.alertContent = '';
        this.messageIcon = null;
        this.hasAnyFieldErrors = false;
        this.submitted = true;

        // Validate with vee-validate v4 (returns { valid, results } or true)
        const result = await validate();
        const isValid = result === true || result?.valid === true;

        if (isValid) {
          await this.post(setErrors, setFieldError);
        } else {
          this.hasAnyFieldErrors = true;
          focusFirstInvalid(this.$refs.form);
        }
      },

      async post(setErrors, setFieldError) {
        const form = this.$refs.form;
        if (!form) return;

        try {
          let payload = null;
          if (!this.submitVerificationCodeModel.isOOBARegistration) {
            payload = { answer: this.model.verificationCode };
          } else {
            payload = { verificationCode: this.model.verificationCode };
          }

          const response = await this.$http.post(this.submitVerificationCodeModel.action, payload);
          // Redirect flow: clear validation UI and exit early
          if (redirectHandler.isRedirect(response)) {
            this.submitted = false;
            this.hasAnyFieldErrors = false;
            this.model.errorMessage = null;
            this.alertContent = '';
            this.messageIcon = null;

            if (typeof setErrors === 'function') {
              setErrors({});
            }
            if (typeof setFieldError === 'function') {
              setFieldError('verifyCode', undefined);
            }

            redirectHandler.checkAndRedirect(response, this.$router);
            return;
          }

          // Update model from server (success/failure banner handling)
          this.model = response.data || {};
          if (!this.model.success) {
            this.messageIcon = 'warning';
            this.alertContent = 'validation-summary-errors alert alert-danger fade show';
          }
        } catch (error) {
          // Prefer vee-validate v4 server mapping (400 ModelState)
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            this.hasAnyFieldErrors = true;
            focusFirstInvalid(this.$refs.form);
            return;
          }
          // Non-400 or unexpected: surface message in banner
          const msg = error?.response?.data?.message || error?.message;
          if (msg) {
            this.model.errorMessage = msg;
            this.messageIcon = 'warning';
            this.alertContent = 'validation-summary-errors alert alert-danger fade show';
          }
          throw error;
        } finally {
          // keep disabled/enabled state default (no spinner in this component)
        }
      },

      async loginCancel(path) {
        const response = await this.$http.post(path);
        redirectHandler.checkAndRedirect(response, this.$router);
      },

      resetValidationUi(setErrors, setFieldError) {
        this.submitted = false;
        this.hasAnyFieldErrors = false;

        if (typeof setErrors === 'function') {
          setErrors({});
        }
        if (typeof setFieldError === 'function') {
          setFieldError('verifyCode', undefined);
        }

        this.model.errorMessage = null;
        this.alertContent = '';
        this.messageIcon = null;
      },

      async resendVerificationCode(path, setErrors, setFieldError) {
        this.resetValidationUi(setErrors, setFieldError);

        try {
          const response = await this.$http.post(path);
          redirectHandler.checkAndRedirect(response, this.$router);

          this.model = response.data || {};
          if (this.model.success) {
            if (this.model.verificationCodeResentCount >= this.model.resendCodeLimit) {
              this.isLinkEnabled = false;
            }
            this.messageIcon = 'check_circle';
            this.alertContent = 'validation-summary-valid alert alert-success fade show';
          }
        } catch (error) {
          const msg = error?.response?.data?.message || error?.message;
          if (msg) {
            this.model.errorMessage = msg;
            this.messageIcon = 'warning';
            this.alertContent = 'validation-summary-errors alert alert-danger fade show';
          }
          throw error;
        }
      },

      closeBanner() {
        this.model && (this.model.errorMessage = null);
      },

      hideToolTip() {
        const helpToolTip = document.getElementsByClassName('tooltip')[0];
        if (helpToolTip && helpToolTip.classList.contains('show')) {
          const helpTrigger = document.getElementsByClassName('help-trigger')[0];
          helpTrigger && helpTrigger.click();
        }
      }
    },

    async created() {
      try {
        let response = null;
        if (this.submitVerificationCode.challengeModel != null) {
          response = this.submitVerificationCode.challengeModel;
        } else {
          response = await this.$http.get(this.submitVerificationCode.action);
        }

        // Expecting response.data to contain otpComplexity and instruction text
        const data = response.data || {};
        this.complexityOtpModel = data.otpComplexity || {};
        this.otpinstructionModel = data;
        this.previousPageUrl = this.otpinstructionModel.previousPageUrl || '';
        this.submitVerificationCodeModel = this.submitVerificationCode;
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message;
        if (msg) {
          this.model.errorMessage = msg;
          this.messageIcon = 'warning';
          this.alertContent = 'validation-summary-errors alert alert-danger fade show';
        }
        throw error;
      }
    }
  };
</script>